import { IReactFrameConnector } from "@multiweb/reactConnectors/types";
import { invariant } from "@multiweb/reactUtils/invariant"; // Nejde to přes index kvůli cyklu. Např. saga.test.ts -> bridge -> ReactConnectorFramesManager -> bridge.
import { CaseInsensitiveMap } from "@multiweb/types/multiweb";

/**
 * Správce instancí {@link IReactFrameConnector}.
 * - Je zodpovědností každé třídy implementující rozhraní {@link IReactFrameConnector}, aby v konstruktoru provolala `register` a v `dispose` `unregister`.
 * - V Reactu si někdy nemůžeme dovolit uchovávat celý frame. Výhodnější může být uchovat jen jeho ID (např. v Reduxu)
 * a mít následně možnost - na základě toho ID - frame získat. Viz {@link ReactConnectorFramesManager.getFrameById}.
 */
export class ReactConnectorFramesManager {
    private static readonly _frames: CaseInsensitiveMap<IReactFrameConnector> = new CaseInsensitiveMap();

    /**
     * Zaregistruje frame pod `frame.element.id`. Díky tomu bude dosupný metodou {@link getFrameById}.
     * Předpoklad: `frame.element.id` je jedinečné. Při pokusu zaregistrovat frame na totéž ID podruhé selže na invariant.
     * @param frame Frame k zaregistrování.
     */
    public static register(frame: IReactFrameConnector): void {
        const id = frame.element.id;
        invariant(!this._frames.get(id), `Frame with given ID is already registered: id="${id}`);
        this._frames.set(id, frame);
    }

    /**
     * Odregistruje frame. Od zavololání této metody již nebude daný frame k dispozici metodou {@link getFrameById}.
     * @param frame Frame k odregistrování.
     */
    public static unregister(frame: IReactFrameConnector): void {
        const id = frame.element.id;
        invariant(!!this._frames.get(id), `Attempt to unregister unregistered frame: id="${id}`);
        this._frames.delete(id);
    }

    /**
     * Vrátí frame se zadaným ID.
     * Předpoklad: Dané ID je zaregistrováno - viz {@link register}. Pokud není, vyhodí chybu.
     * @param id ID zaregistrovaného framu, jež má být vrácen.
     */
    public static getFrameById(id: string): IReactFrameConnector {
        const frame = this._frames.get(id);
        invariant(!!frame, `Frame not found: id="${id}`);
        return frame;
    }
}
